import React from "react";
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	CardActions
} from "@mui/material";
import {
	Edit as EditIcon,
	Visibility as VisibilityIcon
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import { Project } from "../../models/Project";
import ProjectStatusBadge from "../common/ProjectStatusBadge";
import ProjectBudgetDisplay from "./ProjectBudgetDisplay";
import { formatDate } from "./ProjectListTable.utils";

interface ProjectCardProps {
	project: Project;
	showFeePercentage: boolean;
	onViewProject: (id: number) => void;
	onEditProject: (id: number) => void;
	canEditProject: (project: Project) => boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
	project,
	showFeePercentage,
	onViewProject,
	onEditProject,
	canEditProject
}) => {
	const { t } = useTranslation();
	const { navigateByEnvironment } = useEnvironmentNavigation();

	return (
		<Card elevation={2} sx={{ mb: 2 }}>
			<CardContent>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
					<Box>
						<Typography variant="body2" color="primary" fontWeight="bold">
							{project.projectNumber}
						</Typography>
						<Typography 
							variant="h6" 
							onClick={() => onViewProject(project.id)}
							sx={{ 
								mt: 0.5,
								cursor: 'pointer',
								textDecoration: 'none',
								'&:hover': {
									textDecoration: 'underline',
									color: 'primary.main'
								}
							}}
						>
							{project.name}
						</Typography>
					</Box>
					<ProjectStatusBadge status={project.status} />
				</Box>

				<Grid container spacing={2}>
					<Grid size={12}>
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								{t("project.company")}:
							</Typography>
							{project.company ? (
								<Typography 
									variant="body2"
									onClick={() => navigateByEnvironment(`/companies/${project.company!.id}`)}
									sx={{
										cursor: 'pointer',
										textDecoration: 'none',
										'&:hover': {
											textDecoration: 'underline',
											color: 'primary.main'
										}
									}}
								>
									{project.company.tradingName || project.company.legalName}
								</Typography>
							) : (
								<Typography variant="body2">-</Typography>
							)}
						</Box>
					</Grid>

					<Grid size={6}>
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								{t("project.budget")}:
							</Typography>
							<ProjectBudgetDisplay project={project} />
						</Box>
					</Grid>

					<Grid size={6}>
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								{t("project.desiredStartDate")}:
							</Typography>
							<Typography variant="body2">
								{formatDate(project.desiredStartDate)}
							</Typography>
						</Box>
					</Grid>

					{showFeePercentage && (
						<Grid size={6}>
							<Box display="flex" justifyContent="space-between">
								<Typography variant="body2" color="text.secondary">
									{t("project.feePercentage")}:
								</Typography>
								<Typography variant="body2">
									{project.feePercentage ? `${project.feePercentage}%` : "-"}
								</Typography>
							</Box>
						</Grid>
					)}

					<Grid size={showFeePercentage ? 6 : 12}>
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								{t("project.createdAt")}:
							</Typography>
							<Typography variant="body2">{formatDate(project.createdAt)}</Typography>
						</Box>
					</Grid>
				</Grid>
			</CardContent>

			<CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
				<Button
					size="small"
					startIcon={<VisibilityIcon />}
					onClick={() => onViewProject(project.id)}
					color="secondary"
				>
					{t("common.view")}
				</Button>
				{canEditProject(project) && (
					<Button
						size="small"
						startIcon={<EditIcon />}
						onClick={() => onEditProject(project.id)}
						color="primary"
					>
						{t("common.edit")}
					</Button>
				)}
			</CardActions>
		</Card>
	);
};

export default ProjectCard;